import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client';

export type Load<T> =
  | { status: 'loading' }
  | { status: 'ready'; value: T }
  | { status: 'error'; error: { category: string; code: string; message: string } };

/**
 * Chargement d'une lecture de l'API : état explicite (chargement, prêt, erreur), rechargement, et
 * réponse périmée ignorée quand la clé change entre-temps.
 */
export function useLoad<T>(key: string, loader: () => Promise<T>): [Load<T>, () => void] {
  const [state, setState] = useState<Load<T>>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const current = useRef(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    const ticket = ++current.current;
    setState({ status: 'loading' });
    loaderRef.current().then(
      (value) => {
        if (ticket === current.current) setState({ status: 'ready', value });
      },
      (error: unknown) => {
        if (ticket !== current.current) return;
        const normalized = error instanceof ApiError ? error.toServiceError() : { category: 'INTERNAL_ERROR', code: 'CLIENT_ERROR', message: error instanceof Error ? error.message : String(error) };
        setState({ status: 'error', error: normalized });
      },
    );
  }, [key, attempt]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);
  return [state, reload];
}
